import { useTheme } from '@mui/material/styles';
import { URLS, FONTS, LINKS } from '../../config/app-config';
import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Typography, Dialog } from '@mui/material';
import BreadCrumbs from '../filters/BreadCrumbs';
import VirtualArticleList from './VirtualArticleList';
import GlossaryDialog from '../dialogs/GlossaryDialog';
import SnakeGame from '../other/SnakeGame';
import SingleReaderPanel from './SingleReaderPanel';
import { useAuth } from '../../auth/AuthProvider';
import LLMProcessPanel from './LLMProcessPanel';
import { LLM_STATES, useLLMProcess } from '../../contexts/LLMProcessContext';

import { secureFetch } from "../../utils/secureFetch";
import Loader from "../other/Loader";
import useReaderScroll from '../../hooks/useReaderScroll';

export default function ReaderPanel({
    tocData,
    flatToc,
    articles,
    selectedArticleId,
    currentArticleIndex,
    selectedRelease,
    loadNextArticle,
    loadPrevArticle,
    onArticleChange,
    handleArticleSelect,
    isLoading,
    error,
    offlineMode,
    fetchArticle,
}) {
    const theme = useTheme();
    const darkMode = theme.palette.mode === 'dark';
    const containerRef = useRef(null);
    const contentRef = useRef(null);
    const scrollAdjustmentRef = useRef(null);
    useReaderScroll({
        containerRef, contentRef, scrollAdjustmentRef, articles, currentArticleIndex,
        loadNextArticle, onArticleChange, isLoading, error,
    });
    const { user, isAuthenticated } = useAuth();

    const { status, reset } = useLLMProcess();

    const [glossaryOpen, setGlossaryOpen] = useState(false);
    const [glossaryHtml, setGlossaryHtml] = useState('');
    const [glossaryTitle, setGlossaryTitle] = useState('');

    const [singleOpen, setSingleOpen] = useState(false);
    const [singleArticleId, setSingleArticleId] = useState(null);

    const baseUrl = URLS.apiBase.replace(/\/$/, '');

    // Count a view only after an authenticated reader spends 30 seconds on a topic.
    useEffect(() => {
        if (!isAuthenticated) return;
        const timer = setTimeout(() => {
            const entry = flatToc.find(i => i._id === selectedArticleId) || {};
            secureFetch(`${baseUrl}/tracking/article-view`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    article_id: selectedArticleId,
                    title: entry.title || '',
                    url: window.location.href,
                    ...selectedRelease,
                    timestamp: new Date().toISOString(),
                }),
                credentials: 'include'
            }, true).catch(console.error);
        }, 30000);
        return () => clearTimeout(timer);
    }, [selectedArticleId, isAuthenticated, flatToc, baseUrl, user, selectedRelease]);

    // Delegate generated article links because the virtual list mounts content dynamically.
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;
        const onClick = async e => {
            let target = e.target;
            if (target.nodeType === Node.TEXT_NODE) target = target.parentElement;

            const embeddedLink = target.closest('a[data-embeddedarticleid]');
            if (embeddedLink) {
                e.preventDefault();
                setSingleArticleId(embeddedLink.getAttribute('data-embeddedarticleid'));
                setSingleOpen(true);
                return;
            }

            const glossaryLink = target.closest('a[data-glossaryitemid]');
            if (glossaryLink && fetchArticle) {
                e.preventDefault();
                await fetchArticle(glossaryLink.getAttribute('data-glossaryitemid'), (article) => {
                    setGlossaryHtml(article.body || '');
                    setGlossaryTitle(glossaryLink.textContent || 'Glossary Entry');
                    setGlossaryOpen(true);
                }, true);
            }
        };

        container.addEventListener('click', onClick);
        return () => container.removeEventListener('click', onClick);
    }, [fetchArticle]);

    const loaded = Object.keys(articles).map(Number).sort((a, b) => a - b);

    return (
        <>

            {status !== LLM_STATES.idle && (
                <LLMProcessPanel
                    onClose={reset}
                />
            )}

            <Box
                component="main"
                ref={containerRef}
                tabIndex={0}
                sx={{
                    flexGrow: 1,
                    width: '100%',
                    height: '100vh',

                    overflowY: 'auto',
                    overflowAnchor: 'none',

                    borderRadius: 0,
                    bgcolor: 'background.paper',
                    color: darkMode ? '#fff' : 'inherit',
                    // ensure images have white background always
                    '& img': {
                        backgroundColor: '#fff',
                        display: 'block',

                    },
                    // links use brand accent in dark mode
                    ...(darkMode
                        ? { '& a': { color: 'accent.main' } }
                        : {}
                    ),
                    '&::-webkit-scrollbar': { width: '8px' },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: darkMode ? '#444' : '#9aa',
                        borderRadius: '4px'
                    },
                    scrollbarWidth: 'thin',
                    scrollbarColor: darkMode ? '#444 transparent' : '#9aa transparent',
                }}
            >
                <BreadCrumbs
                    tocData={tocData}
                    selectedRelease={selectedRelease}
                    selectedArticleId={selectedArticleId}
                    handleArticleSelect={handleArticleSelect}
                />

                {error && (
                    <Box sx={{ p: 2, textAlign: 'center', color: darkMode ? '#fff' : 'inherit' }}>
                        <Typography variant="h4" sx={{ mb: 2, fontFamily: FONTS.heading }}>
                            Error
                        </Typography>
                        <Typography sx={{ fontFamily: FONTS.body }}>
                            Sorry, but it seems like there is an issue loading that content.
                        </Typography>
                        {LINKS.supportEmail && (
                            <Typography sx={{ fontFamily: FONTS.body }}>
                                If this issue persists, reach us at <b>{LINKS.supportEmail}</b>.
                            </Typography>
                        )}
                        <Typography sx={{ mt: 2, fontWeight: 'bold' }}>{error}</Typography>
                        <SnakeGame />
                    </Box>
                )}

                {loaded.length === 0 && isLoading && (
                    <Box
                        sx={{
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <Loader
                            marginCustom='10px 0 0 0'
                        />
                    </Box>
                )}

                {loaded.length > 0 && loaded[0] > 0 && (
                    <Box sx={{ p: 2, textAlign: 'center' }}>
                        <Button
                            variant="outlined"
                            onClick={loadPrevArticle}
                            sx={{
                                color: 'accent.main',
                                borderColor: 'accent.main'
                            }}
                        >
                            Load Previous Topics
                        </Button>
                    </Box>
                )}

                <Box ref={contentRef}>
                    <VirtualArticleList
                        key={`${selectedRelease.product}:${selectedRelease.version}:${selectedRelease.language}`}
                        articles={articles}
                        flatToc={flatToc}
                        containerRef={containerRef}
                        contentRef={contentRef}
                        scrollAdjustmentRef={scrollAdjustmentRef}
                        selectedRelease={selectedRelease}
                        offlineMode={offlineMode}
                    />
                </Box>

                {isLoading && loaded.length > 0 && (
                    <Loader />
                )}
            </Box>

            <GlossaryDialog
                open={glossaryOpen}
                onClose={() => setGlossaryOpen(false)}
                contentHtml={glossaryHtml}
                title={glossaryTitle}
            />

            <Dialog fullWidth maxWidth="md" open={singleOpen} onClose={() => setSingleOpen(false)}>
                {singleArticleId && (
                    <SingleReaderPanel
                        articleId={singleArticleId}
                        selectedRelease={selectedRelease}
                        fetchArticle={fetchArticle}
                        handleArticleSelect={handleArticleSelect}
                        onClose={() => setSingleOpen(false)}
                    />
                )}
            </Dialog>
        </>
    );
}

